import React, { useState, useEffect, useCallback } from 'react';
import {
  MapPin, Plus, Trash2, Edit2, Search, Loader,
  Satellite, Sun, Check, X, ChevronDown, ChevronRight,
  RefreshCw, AlertCircle
} from 'lucide-react';

const API_BASE = 'https://apisaleeasy.psisat.com';

// ────────────────────────────────────────────────────────────────────────────
// Utility
// ────────────────────────────────────────────────────────────────────────────

const Toggle = ({ checked, onChange, color = 'blue' }) => {
  const colors = {
    blue:  'bg-blue-500',
    green: 'bg-green-500',
  };
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
        checked ? colors[color] : 'bg-gray-300'
      }`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
          checked ? 'translate-x-6' : 'translate-x-1'
        }`}
      />
    </button>
  );
};

// ────────────────────────────────────────────────────────────────────────────
// Modal เพิ่ม / แก้ไขพิกัด
// ────────────────────────────────────────────────────────────────────────────

const LocationFormModal = ({ technicianCode, existingLocation, onSave, onClose }) => {
  const isEdit = !!existingLocation;
  const [form, setForm] = useState({
    label:        existingLocation?.label        ?? '',
    latitude:     existingLocation?.latitude     ?? '',
    longitude:    existingLocation?.longitude    ?? '',
    showmap_sate:  existingLocation?.showmap_sate  ?? false,
    showmap_solar: existingLocation?.showmap_solar ?? false,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const lat = parseFloat(form.latitude);
    const lng = parseFloat(form.longitude);
    if (isNaN(lat) || isNaN(lng)) {
      setError('latitude / longitude ต้องเป็นตัวเลข');
      return;
    }
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
      setError('ค่าพิกัดไม่ถูกต้อง');
      return;
    }
    if (!form.showmap_sate && !form.showmap_solar) {
      setError('กรุณาเปิดอย่างน้อย 1 Tab (ดาวเทียม หรือ โซลาร์เซลล์)');
      return;
    }

    setSaving(true);
    try {
      const url = isEdit
        ? `${API_BASE}/api/technician-map-locations/${existingLocation.id}`
        : `${API_BASE}/api/technician-map-locations`;
      const method = isEdit ? 'PUT' : 'POST';

      const body = {
        technician_code: technicianCode,
        label:        form.label || null,
        latitude:     lat,
        longitude:    lng,
        showmap_sate:  form.showmap_sate,
        showmap_solar: form.showmap_solar,
        updated_by:   'admin',
      };

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.message || 'เกิดข้อผิดพลาด');
      onSave(json.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b">
          <div className="flex items-center gap-2">
            <MapPin className="w-5 h-5 text-blue-600" />
            <h3 className="text-lg font-semibold text-gray-900">
              {isEdit ? 'แก้ไขพิกัด' : 'เพิ่มพิกัดใหม่'}
            </h3>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {/* Label */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              ชื่อสาขา / พิกัด <span className="text-gray-400">(ไม่บังคับ)</span>
            </label>
            <input
              type="text"
              placeholder="เช่น สาขาเชียงใหม่, ออฟฟิศหลัก"
              value={form.label}
              onChange={(e) => setForm(f => ({ ...f, label: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none"
            />
          </div>

          {/* Lat / Lng */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Latitude <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                step="any"
                placeholder="13.7563"
                value={form.latitude}
                onChange={(e) => setForm(f => ({ ...f, latitude: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Longitude <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                step="any"
                placeholder="100.5018"
                value={form.longitude}
                onChange={(e) => setForm(f => ({ ...f, longitude: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none"
                required
              />
            </div>
          </div>

          {/* Map link helper */}
          <p className="text-xs text-gray-400">
            💡 เปิด Google Maps → คลิกขวาที่จุด → คัดลอก Lat, Lng
          </p>

          {/* Tab flags */}
          <div className="bg-gray-50 rounded-xl p-4 space-y-3">
            <p className="text-sm font-medium text-gray-700">แสดงใน Tab แผนที่</p>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Satellite className="w-4 h-4 text-blue-500" />
                <span className="text-sm text-gray-700">ดาวเทียม</span>
              </div>
              <Toggle
                checked={form.showmap_sate}
                onChange={(v) => setForm(f => ({ ...f, showmap_sate: v }))}
                color="blue"
              />
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sun className="w-4 h-4 text-yellow-500" />
                <span className="text-sm text-gray-700">โซลาร์เซลล์</span>
              </div>
              <Toggle
                checked={form.showmap_solar}
                onChange={(v) => setForm(f => ({ ...f, showmap_solar: v }))}
                color="green"
              />
            </div>
          </div>

          {error && (
            <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg p-3">
              <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors font-medium text-sm"
            >
              ยกเลิก
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium text-sm disabled:opacity-50"
            >
              {saving ? <Loader className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              {isEdit ? 'บันทึก' : 'เพิ่มพิกัด'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ────────────────────────────────────────────────────────────────────────────
// รายการพิกัดของ technician คนหนึ่ง
// ────────────────────────────────────────────────────────────────────────────

const TechnicianCard = ({ technician, currentUser }) => {
  const [expanded, setExpanded]       = useState(false);
  const [locations, setLocations]     = useState([]);
  const [loading, setLoading]         = useState(false);
  const [showForm, setShowForm]       = useState(false);
  const [editTarget, setEditTarget]   = useState(null);
  const [deletingId, setDeletingId]   = useState(null);

  const loadLocations = useCallback(async () => {
    setLoading(true);
    try {
      const res  = await fetch(`${API_BASE}/api/technician-map-locations/${technician.code}`);
      const json = await res.json();
      setLocations(json.success ? json.data : []);
    } catch {
      setLocations([]);
    } finally {
      setLoading(false);
    }
  }, [technician.code]);

  const handleExpand = () => {
    const next = !expanded;
    setExpanded(next);
    if (next && locations.length === 0) loadLocations();
  };

  const handleSave = (saved) => {
    setShowForm(false);
    setEditTarget(null);
    loadLocations();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('ลบพิกัดนี้?')) return;
    setDeletingId(id);
    try {
      const res  = await fetch(`${API_BASE}/api/technician-map-locations/${id}`, { method: 'DELETE' });
      const json = await res.json();
      if (json.success) setLocations(prev => prev.filter(l => l.id !== id));
      else alert(json.message || 'ลบไม่สำเร็จ');
    } catch {
      alert('เกิดข้อผิดพลาด');
    } finally {
      setDeletingId(null);
    }
  };

  const activeCount = locations.filter(l => l.is_active).length;

  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
      {/* Header row */}
      <button
        onClick={handleExpand}
        className="w-full flex items-center gap-4 p-4 hover:bg-gray-50 transition-colors text-left"
      >
        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
          <span className="text-blue-700 font-bold text-sm">
            {technician.name?.charAt(0) || '?'}
          </span>
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-gray-900 truncate">
            {technician.name} {technician.surname}
          </p>
          <p className="text-xs text-gray-500">{technician.code} · {technician.province}</p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {expanded && (
            <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full font-medium">
              {activeCount} พิกัด
            </span>
          )}
          {expanded
            ? <ChevronDown className="w-4 h-4 text-gray-400" />
            : <ChevronRight className="w-4 h-4 text-gray-400" />
          }
        </div>
      </button>

      {/* Expanded detail */}
      {expanded && (
        <div className="border-t border-gray-100 bg-gray-50 p-4 space-y-3">
          {/* Add button */}
          <div className="flex justify-end">
            <button
              onClick={() => { setEditTarget(null); setShowForm(true); }}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors"
            >
              <Plus className="w-4 h-4" />
              เพิ่มพิกัดร้านค้า
            </button>
          </div>

          {/* Location list */}
          {loading ? (
            <div className="flex justify-center py-4">
              <Loader className="w-5 h-5 animate-spin text-blue-500" />
            </div>
          ) : locations.length === 0 ? (
            <p className="text-center text-gray-400 text-sm py-4">ยังไม่มีพิกัด — กด "เพิ่มพิกัดร้านค้า"</p>
          ) : (
            <div className="space-y-2">
              {locations.map((loc) => (
                <div
                  key={loc.id}
                  className={`flex items-start gap-3 p-3 rounded-lg border ${
                    loc.is_active ? 'bg-white border-gray-200' : 'bg-gray-100 border-gray-200 opacity-60'
                  }`}
                >
                  <MapPin className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800">
                      {loc.label || <span className="text-gray-400 italic">ไม่มีชื่อสาขา</span>}
                    </p>
                    <p className="text-xs text-gray-500">
                      {parseFloat(loc.latitude).toFixed(6)}, {parseFloat(loc.longitude).toFixed(6)}
                    </p>
                    <div className="flex gap-2 mt-1">
                      {loc.showmap_sate ? (
                        <span className="inline-flex items-center gap-1 text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">
                          <Satellite className="w-3 h-3" /> ดาวเทียม
                        </span>
                      ) : null}
                      {loc.showmap_solar ? (
                        <span className="inline-flex items-center gap-1 text-xs bg-yellow-50 text-yellow-700 px-2 py-0.5 rounded-full">
                          <Sun className="w-3 h-3" /> โซลาร์เซลล์
                        </span>
                      ) : null}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={() => { setEditTarget(loc); setShowForm(true); }}
                      className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="แก้ไข"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(loc.id)}
                      disabled={deletingId === loc.id}
                      className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                      title="ลบ"
                    >
                      {deletingId === loc.id
                        ? <Loader className="w-4 h-4 animate-spin" />
                        : <Trash2 className="w-4 h-4" />
                      }
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Form modal */}
      {showForm && (
        <LocationFormModal
          technicianCode={technician.code}
          existingLocation={editTarget}
          onSave={handleSave}
          onClose={() => { setShowForm(false); setEditTarget(null); }}
        />
      )}
    </div>
  );
};

// ────────────────────────────────────────────────────────────────────────────
// Main Component
// ────────────────────────────────────────────────────────────────────────────

const TechnicianMapManagementComponent = ({ currentUser }) => {
  const [technicians, setTechnicians] = useState([]);
  const [filtered, setFiltered]       = useState([]);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState('');
  const [search, setSearch]           = useState('');

  const loadTechnicians = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res  = await fetch(`${API_BASE}/api/technician`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ page: 1, limit: 1000, type: 'all' }),
      });
      const json = await res.json();
      if (!json.success && !json.status) throw new Error(json.message || 'โหลดข้อมูลไม่สำเร็จ');

      // รองรับทั้ง 2 response format
      const raw = json.data?.data ?? json.data ?? [];
      const list = raw.map(t => ({
        code:     t.technician_code,
        name:     t.technician_name,
        surname:  t.technician_surname,
        phone:    t.technician_phone,
        province: t.technician_province,
      }));
      setTechnicians(list);
      setFiltered(list);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadTechnicians(); }, [loadTechnicians]);

  useEffect(() => {
    const q = search.trim().toLowerCase();
    if (!q) { setFiltered(technicians); return; }
    setFiltered(technicians.filter(t =>
      `${t.name} ${t.surname}`.toLowerCase().includes(q) ||
      t.code.toLowerCase().includes(q) ||
      (t.province || '').toLowerCase().includes(q) ||
      (t.phone || '').includes(q)
    ));
  }, [search, technicians]);

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <MapPin className="w-6 h-6 text-blue-600" />
            จัดการพิกัดแผนที่ตัวแทน
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            เพิ่ม / แก้ไข / ลบพิกัดร้านค้าที่แสดงบน Tab ดาวเทียม และ โซลาร์เซลล์
          </p>
        </div>
        <button
          onClick={loadTechnicians}
          disabled={loading}
          className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors text-sm"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          รีเฟรช
        </button>
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          placeholder="ค้นหาชื่อ, รหัส, จังหวัด, เบอร์โทร..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-9 pr-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none"
        />
      </div>

      {/* Stats */}
      {!loading && technicians.length > 0 && (
        <p className="text-xs text-gray-400 mb-4">
          แสดง {filtered.length} จาก {technicians.length} ร้านค้า
          {search && ` — ค้นหา "${search}"`}
        </p>
      )}

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex flex-col items-center justify-center py-16 text-gray-400">
          <Loader className="w-8 h-8 animate-spin mb-3" />
          <p className="text-sm">กำลังโหลดรายชื่อตัวแทน...</p>
        </div>
      )}

      {/* List */}
      {!loading && (
        <div className="space-y-2">
          {filtered.length === 0 ? (
            <div className="text-center py-16 text-gray-400">
              <MapPin className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>{search ? `ไม่พบ "${search}"` : 'ไม่มีข้อมูลตัวแทน'}</p>
            </div>
          ) : (
            filtered.map(t => (
              <TechnicianCard key={t.code} technician={t} currentUser={currentUser} />
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default TechnicianMapManagementComponent;
